import api, { UPLOAD_TIMEOUT } from './api'
import { Client } from '@stomp/stompjs'

// API base - don't include /api since api instance already has it
const API_BASE = '/messages'

// WebSocket connection state management
let stompClient: Client | null = null
let messageHandlers: Array<(data: any) => void> = []
let typingHandlers: Array<(data: any) => void> = []
let connectionHandlers: Array<(connected: boolean) => void> = []
let subscriptions: Record<string, any> = {}
let reconnectAttempts = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let isConnecting = false
let pendingMessages: any[] = []

const MAX_RECONNECT_ATTEMPTS = 10
const INITIAL_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000
const HEARTBEAT_INTERVAL = 30000

const getReconnectDelay = (attempts: number) => {
  const delay = Math.min(INITIAL_RECONNECT_DELAY * Math.pow(2, attempts), MAX_RECONNECT_DELAY)
  return delay + Math.random() * 1000
}

// Get WebSocket URL - use Vite proxy in dev, direct connection in prod
const getWebSocketUrl = () => {
  const isDev = import.meta.env.DEV
  if (isDev) {
    // Use Vite proxy: /ws -> ws://localhost:8091/api
    // So /ws/stomp becomes ws://localhost:8091/api/ws/stomp
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${window.location.host}/ws/stomp`
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  return `${protocol}//${host}/api/ws/stomp`
}

const chatService = {
  // ==================== WEBSOCKET ====================

  connectWebSocket: (userId: number, token: string | null): Promise<Client> => {
    if (stompClient && stompClient.connected) {
      return Promise.resolve(stompClient)
    }

    if (isConnecting) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (stompClient && stompClient.connected) {
            clearInterval(checkInterval)
            resolve(stompClient)
          }
        }, 100)
      })
    }

    isConnecting = true

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `${getWebSocketUrl()}?userId=${userId}&token=${token || ''}`

        console.log('Connecting to WebSocket:', wsUrl)

        stompClient = new Client({
          brokerURL: wsUrl,
          connectHeaders: {
            userId: userId.toString(),
            token: token || ''
          },
          debug: (str: string) => {
            if (str.includes('Error') || str.includes('Connect') || str.includes('Disconnect')) {
              console.log('STOMP:', str)
            }
          },
          onConnect: () => {
            console.log('STOMP Connected successfully')
            reconnectAttempts = 0
            isConnecting = false
            connectionHandlers.forEach(h => h(true))

            const userTopic = `/user/${userId}/queue/messages`
            if (stompClient && stompClient.connected) {
              stompClient.subscribe(userTopic, (message: any) => {
                try {
                  const data = JSON.parse(message.body)
                  console.log('Received user message:', data)
                  messageHandlers.forEach(h => h(data))
                } catch (e) {
                  console.error('Error parsing user message:', e)
                }
              })
              console.log('Subscribed to user topic:', userTopic)
            }

            if (pendingMessages.length > 0) {
              console.log(`Sending ${pendingMessages.length} pending messages`)
              const messagesToSend = [...pendingMessages]
              pendingMessages = []
              messagesToSend.forEach(msg => {
                chatService.sendMessage(msg.data).catch(err => {
                  console.error('Failed to send pending message:', err)
                  pendingMessages.push(msg)
                })
              })
            }

            resolve(stompClient!)
          },
          onStompError: (frame: any) => {
            console.error('STOMP Error:', frame.headers?.message || frame)
            isConnecting = false
            connectionHandlers.forEach(h => h(false))
            reject(new Error(frame.headers?.message || 'STOMP error'))
          },
          onWebSocketError: (event: any) => {
            console.error('WebSocket Error:', event)
            isConnecting = false
          },
          onWebSocketClose: () => {
            console.log('WebSocket Disconnected')
            isConnecting = false
            connectionHandlers.forEach(h => h(false))

            if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
              const delay = getReconnectDelay(reconnectAttempts)
              console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`)
              reconnectTimer = setTimeout(() => {
                reconnectAttempts++
                const storedToken = localStorage.getItem('token')
                const storedUser = localStorage.getItem('user')
                if (storedUser) {
                  try {
                    const user = JSON.parse(storedUser)
                    chatService.connectWebSocket(user.userId || user.id, storedToken)
                  } catch (e) {
                    console.error('Error parsing stored user:', e)
                  }
                }
              }, delay)
            }
          },
          onDisconnect: () => {
            console.log('STOMP Disconnected')
            isConnecting = false
          },
          heartbeatIncoming: HEARTBEAT_INTERVAL,
          heartbeatOutgoing: HEARTBEAT_INTERVAL,
          reconnectDelay: 0
        })

        stompClient.activate()
      } catch (error) {
        console.error('Error creating STOMP client:', error)
        isConnecting = false
        reject(error)
      }
    })
  },

  disconnectWebSocket: () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    reconnectAttempts = MAX_RECONNECT_ATTEMPTS // Prevent reconnection

    if (stompClient) {
      stompClient.deactivate()
      stompClient = null
    }
    subscriptions = {}
    messageHandlers = []
    typingHandlers = []
    connectionHandlers = []
  },

  isConnected: () => stompClient?.connected || false,

  onMessage: (handler: (data: any) => void) => {
    messageHandlers.push(handler)
    return () => {
      messageHandlers = messageHandlers.filter(h => h !== handler)
    }
  },

  onTyping: (handler: (data: any) => void) => {
    typingHandlers.push(handler)
    return () => {
      typingHandlers = typingHandlers.filter(h => h !== handler)
    }
  },

  onConnectionChange: (handler: (connected: boolean) => void) => {
    connectionHandlers.push(handler)
    return () => {
      connectionHandlers = connectionHandlers.filter(h => h !== handler)
    }
  },

  joinRoom: (roomId: string | number) => {
    if (stompClient && stompClient.connected) {
      const topic = `/topic/room/${roomId}`
      if (!subscriptions[topic]) {
        subscriptions[topic] = stompClient.subscribe(topic, (message: any) => {
          try {
            const data = JSON.parse(message.body)
            messageHandlers.forEach(h => h(data))
          } catch (e) {
            console.error('Error parsing room message:', e)
          }
        })
        console.log('Joined room:', topic)
      }
    }
  },

  leaveRoom: (roomId: string | number) => {
    const topic = `/topic/room/${roomId}`
    if (subscriptions[topic]) {
      subscriptions[topic].unsubscribe()
      delete subscriptions[topic]
    }
  },

  setTypingStatus: (roomId: string | number, isTyping: boolean) => {
    if (stompClient && stompClient.connected) {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        const user = JSON.parse(storedUser)
        stompClient.publish({
          destination: `/app/typing/${roomId}`,
          body: JSON.stringify({
            userId: user.userId || user.id,
            userName: user.userFirstName || user.firstName || 'User',
            isTyping
          })
        })
      }
    }
  },

  // ==================== REST API ====================

  getConversations: async (page = 0, size = 20) => {
    const response = await api.get(`${API_BASE}/conversations?page=${page}&size=${size}`)
    return response.data
  },

  getConversation: async (conversationId: number) => {
    const response = await api.get(`${API_BASE}/conversation/${conversationId}`)
    return response.data
  },

  getOrCreateDirectConversation: async (otherUserId: number) => {
    const response = await api.post(`${API_BASE}/conversation?userId=${otherUserId}`)
    return response.data
  },

  createGroupConversation: async (name: string, participantIds: number[]) => {
    const response = await api.post(`${API_BASE}/conversation/group?name=${encodeURIComponent(name)}&participantIds=${participantIds.join(',')}`)
    return response.data
  },

  // Get-or-create the GROUP chat linked to a community group (/groups), then open it.
  // Member-only (backend enforces). Used by the "Chat" button on a joined group.
  getOrCreateCommunityGroupConversation: async (communityGroupId: number) => {
    const response = await api.post(`${API_BASE}/conversation/group-by-community/${communityGroupId}`)
    return response.data
  },

  getMessages: async (conversationId: number, page = 0, size = 50, before?: string) => {
    let url = `${API_BASE}/conversation/${conversationId}/messages?page=${page}&size=${size}`
    if (before) url += `&before=${encodeURIComponent(before)}`
    const response = await api.get(url)
    return response.data
  },

  sendMessage: async (data: {
    content: string
    messageType?: string
    receiverId?: number | null
    groupId?: number | null
    roomId?: number | null
    replyToId?: number | null
    conversationId?: number
  }) => {
    const response = await api.post(`${API_BASE}/send`, data)
    return response.data
  },

  sendMediaMessage: async (formData: FormData) => {
    const response = await api.post(`${API_BASE}/send-media`, formData, {
      timeout: UPLOAD_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // Alternative method without Content-Type header (let axios handle it)
  sendMediaMessageAuto: async (formData: FormData) => {
    const response = await api.post(`${API_BASE}/send-media`, formData, {
      timeout: UPLOAD_TIMEOUT
    })
    return response.data
  },

  editMessage: async (messageId: number, content: string) => {
    const response = await api.put(`${API_BASE}/${messageId}?content=${encodeURIComponent(content)}`)
    return response.data
  },

  deleteMessage: async (messageId: number, deleteFor: 'me' | 'everyone' = 'me') => {
    const response = await api.delete(`${API_BASE}/${messageId}?deleteFor=${deleteFor}`)
    return response.data
  },

  markAsRead: async (conversationId: number, messageId?: number) => {
    const url = messageId
      ? `${API_BASE}/conversation/${conversationId}/read?messageId=${messageId}`
      : `${API_BASE}/conversation/${conversationId}/read`
    const response = await api.post(url)
    return response.data
  },

  getUnreadCount: async () => {
    const response = await api.get(`${API_BASE}/unread-count`)
    return response.data
  },

  searchMessages: async (query: string) => {
    const response = await api.get(`${API_BASE}/search?q=${encodeURIComponent(query)}`)
    return response.data
  },

  searchUsers: async (query: string) => {
    const response = await api.get(`/users/search?keyword=${encodeURIComponent(query)}`)
    return response.data
  },

  muteConversation: async (conversationId: number, mute: boolean) => {
    const response = await api.post(`${API_BASE}/conversation/${conversationId}/mute?mute=${mute}`)
    return response.data
  },

  pinConversation: async (conversationId: number, pin: boolean) => {
    const response = await api.post(`${API_BASE}/conversation/${conversationId}/pin?pin=${pin}`)
    return response.data
  },

  addReaction: async (messageId: number, emoji: string) => {
    const response = await api.post(`${API_BASE}/${messageId}/reaction?emoji=${emoji}`)
    return response.data
  },

  leaveGroup: async (conversationId: number) => {
    const response = await api.post(`${API_BASE}/conversation/${conversationId}/leave`)
    return response.data
  },

  getOnlineStatus: async (userId: number) => {
    const response = await api.get(`/users/${userId}/online`)
    return response.data
  },

  deleteConversation: async (conversationId: number) => {
    const response = await api.delete(`${API_BASE}/conversation/${conversationId}`)
    return response.data
  }
}

export default chatService